import { create } from "zustand";
import { toast } from "react-toastify";
import {
  COMPLAINT_ENDPOINTS,
  getData,
  postData,
  putData,
} from "../../../../Apis/complaintApi";
import {
  COMPLAINT_MAX_FETCH_SIZE,
  paginateClientList,
  sortActiveFirst,
  buildClientPagination,
} from "../../utils/complaintListUtils";

const MOCK_CATEGORY_OPTIONS = [
  { id: 1, name: "Complaint" },
  { id: 2, name: "Service" },
  { id: 3, name: "Infrastructure" },
  { id: 4, name: "Feedback" },
  { id: 5, name: "Sanitation" },
  { id: 6, name: "Waste Management" },
];

const mapSlaCategoryItem = (item) => ({
  slaCategoryId: item.slacategoryId ?? item.slaCategoryId,
  categoryName: item.name ?? item.categoryName ?? "",
  durationInHr: item.durationInHours ?? item.durationInHr,
  startTime: item.startTime ?? "",
  endTime: item.endTime ?? "",
  isActive: Boolean(item.isActive),
});

const mapSlaCategoryList = (response = {}) =>
  (response.data ?? []).map(mapSlaCategoryItem);

const applyCategorySearch = (data, searchTerm) => {
  const term = searchTerm?.toLowerCase()?.trim();
  if (!term) return data;

  return data.filter((item) =>
    item?.categoryName?.toLowerCase()?.includes(term)
  );
};

export const formatTimeTo12Hour = (time) => {
  if (!time) return "N/A";
  if (/AM|PM/i.test(time)) return time;

  const [hours, minutes] = time.split(":");
  if (!hours || minutes === undefined) return time;

  const parsedHours = parseInt(hours, 10);
  const period = parsedHours >= 12 ? "PM" : "AM";
  const formattedHours = parsedHours % 12 || 12;
  const formattedMinutes = minutes.padStart(2, "0");

  return `${String(formattedHours).padStart(2, "0")}:${formattedMinutes} ${period}`;
};

export const formatTimeForApi = (time) => {
  if (!time) return "";
  if (/AM|PM/i.test(time)) return time;

  const formatted = formatTimeTo12Hour(time);
  return formatted === "N/A" ? time : formatted;
};

export const parseTimeForInput = (time) => {
  if (!time) return "";
  if (!/AM|PM/i.test(time)) return time;

  const match = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return "";

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = match[3].toUpperCase();

  if (period === "PM" && hours !== 12) hours += 12;
  if (period === "AM" && hours === 12) hours = 0;

  return `${String(hours).padStart(2, "0")}:${minutes}`;
};

export const useSlaCategoryStore = create((set, get) => ({
  slaCategories: [],
  filterOriginalSlaCategories: [],
  filteredSlaCategories: [],
  categoryOptions: MOCK_CATEGORY_OPTIONS,
  isLoading: false,
  isSaving: false,
  filters: {
    searchTerm: "",
  },
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
  },

  updateFilter: (name, value) =>
    set((state) => ({
      filters: { ...state.filters, [name]: value },
    })),

  searchSlaCategoryHandler: (searchTerm) => {
    const { filterOriginalSlaCategories, pagination } = get();
    const filtered = applyCategorySearch(filterOriginalSlaCategories, searchTerm);
    const pagedRecords = paginateClientList(filtered, 1, pagination.pageSize);

    set({
      filters: { searchTerm },
      filteredSlaCategories: pagedRecords,
      pagination: buildClientPagination(
        filtered.length,
        1,
        pagination.pageSize
      ),
    });
  },

  fetchSlaCategories: async (options = {}) => {
    const { filters, pagination } = get();
    const currentPage = options.currentPage ?? pagination.currentPage;
    const pageSize = options.pageSize ?? pagination.pageSize;

    set({ isLoading: true });

    try {
      const response = await getData(COMPLAINT_ENDPOINTS.GET_SLA_CATEGORIES, {
        PageIndex: 1,
        PageSize: COMPLAINT_MAX_FETCH_SIZE,
      });

      const sorted = sortActiveFirst(mapSlaCategoryList(response));
      const filtered = applyCategorySearch(sorted, filters.searchTerm);
      const pagedRecords = paginateClientList(filtered, currentPage, pageSize);

      set({
        slaCategories: sorted,
        filterOriginalSlaCategories: sorted,
        filteredSlaCategories: pagedRecords,
        pagination: buildClientPagination(filtered.length, currentPage, pageSize),
        isLoading: false,
      });
    } catch {
      set({
        slaCategories: [],
        filterOriginalSlaCategories: [],
        filteredSlaCategories: [],
        pagination: { ...get().pagination, totalCount: 0 },
        isLoading: false,
      });
    }
  },

  saveSlaCategory: async (payload, isEdit = false) => {
    set({ isSaving: true });

    try {
      if (isEdit) {
        await putData(COMPLAINT_ENDPOINTS.UPDATE_SLA_CATEGORY, {
          slacategoryId: payload.slaCategoryId,
          name: payload.categoryName,
          durationInHours: payload.durationInHr,
          startTime: formatTimeForApi(payload.startTime),
          endTime: formatTimeForApi(payload.endTime),
          isActive: payload.isActive ?? true,
        });
        toast.success("SLA category updated successfully.");
      } else {
        await postData(COMPLAINT_ENDPOINTS.ADD_SLA_CATEGORY, {
          name: payload.categoryName,
          durationInHours: payload.durationInHr,
          startTime: formatTimeForApi(payload.startTime),
          endTime: formatTimeForApi(payload.endTime),
          isActive: payload.isActive ?? true,
        });
        toast.success("SLA category added successfully.");
      }

      await get().fetchSlaCategories();
      return { success: true };
    } catch (error) {
      const message =
        typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.message ||
            "Unable to save SLA category details.";
      toast.error(message);
      return { success: false };
    } finally {
      set({ isSaving: false });
    }
  },
}));
